import type { ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@/lib/utils";
import { AnimatedNumber } from "./AnimatedNumber";
import { IconBubble } from "./IconBubble";
import { DURATION, EASE_SIGNATURE } from "./motion";
import type { Domain } from "./Tile";

interface MeterRowProps {
  label: string;
  value: number;
  unit?: string;
  max: number;
  domain?: Domain;
  icon?: ReactNode;
  precision?: number;
  className?: string;
}

/**
 * A horizontal meter row: optional icon bubble + label on the left,
 * animated value + unit on the right, and a thin proportional fill bar below.
 *
 * Used for top-consumer lists and energy stat bars.
 */

const fillColor: Record<Domain, string> = {
  neutral: "bg-foreground/40",
  light: "bg-domain-light",
  climate: "bg-domain-climate",
  energy: "bg-domain-energy",
  security: "bg-domain-security",
  flow: "bg-domain-flow",
};

export function MeterRow({
  label,
  value,
  unit,
  max,
  domain = "neutral",
  icon,
  precision = 0,
  className,
}: MeterRowProps) {
  const reduced = useReducedMotion();

  // Guard against divide-by-zero or negative max.
  const ratio = max <= 0 ? 0 : Math.min(1, Math.max(0, value / max));
  const fillPercent = ratio * 100;

  return (
    <div data-slot="meter-row" className={cn("flex flex-col gap-1.5", className)}>
      {/* Top row: icon + label on the left, value + unit on the right */}
      <div className="flex items-center gap-2">
        {icon !== null && icon !== undefined && (
          <IconBubble domain={domain} size="sm">
            {icon}
          </IconBubble>
        )}
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{label}</span>
        <span className="shrink-0 text-sm font-semibold tabular-nums">
          <AnimatedNumber value={value} precision={precision} />
          {unit && <span className="ml-0.5 text-xs font-normal text-muted-foreground">{unit}</span>}
        </span>
      </div>

      {/* Fill bar */}
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
      >
        <motion.div
          className={cn("h-full rounded-full", fillColor[domain])}
          style={{ width: `${fillPercent}%` }}
          animate={{ width: `${fillPercent}%` }}
          transition={
            reduced
              ? { duration: 0 }
              : {
                  duration: DURATION.base,
                  ease: EASE_SIGNATURE as [number, number, number, number],
                }
          }
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
